<template>
  <article class="grid-wrapper">
    <section class="grid-container">
      <grid-header />
      <component
        v-bind:is="currentTabComponent"
        @showTooltip="onShowTooltip"
        @hideTooltip="onHideTooltip"
      ></component>
    </section>
    <fullscreen-modal />
    <modal />
    <notification />
  </article>
</template>

<script>
import { mapState } from 'vuex';
import { find, includes } from 'lodash';

import FullscreenModal from '@/components/atoms/FullscreenModal';
import Modal from '@/components/atoms/Modal';
import Notification from '@/components/atoms/Notification';
import GridHeader from '@/components/organisms/GridHeader';
import GridUniverse from '@/components/templates/GridUniverse';
import GridLineups from '@/components/templates/GridLineups';
import GridReplacements from '@/components/templates/GridReplacements';
import GridReplacementsNew from '@/components/templates/GridReplacementsNew';
import GridLineupChanges from '@/components/templates/GridLineupChanges';
import GridFundReview from '@/components/templates/GridFundReview';
import { EventBus } from '@/utils/event-bus';
import { grid } from '@/config/grid-consts.json';

export default {
  name: 'Grid',
  components: {
    GridHeader,
    GridUniverse,
    GridLineups,
    GridReplacements,
    GridReplacementsNew,
    GridLineupChanges,
    GridFundReview,
    FullscreenModal,
    Modal,
    Notification,
  },
  props: {
    clientId: {
      validator: (value) => {
        return !Number.isNaN(+value);
      },
    },
  },
  watch: {
    async $route(to) {
      if (to.path === '/') {
        await this.updateClient(
          grid.allClientViewId,
          grid.allClientViewName,
        );
      } else if (this.isValidClientId) {
        await this.updateClient(
          this.clientData.id,
          this.clientData.client.clientName,
          this.clientData.client.clientCode,
          this.clientData.hasReplacements,
        );
      } else {
        // redirecting to 404
        await this.$router.push({
          name: 'notFound',
        });
      }
    },
  },
  computed: {
    ...mapState({
      tab: (state) => state.grid.tab,
      clientList: (state) => state.grid.clientList,
    }),
    currentTabComponent() {
      return `grid-${this.tab}`;
    },
    clientIds() {
      return this.clientList.map((e) => {
        return e.id;
      });
    },
    clientData() {
      return find(this.clientList, (client) => client.id === +this.clientId);
    },
    isValidClientId() {
      return includes(this.clientIds, +this.clientId);
    },
  },
  methods: {
    onShowTooltip(...args) {
      this.$emit('showTooltip', ...args);
    },
    onHideTooltip() {
      this.$emit('hideTooltip');
    },
    async eraseGridData() {
      await this.$store.dispatch('setClient', {
        listVisible: false,
        name: grid.allClientViewName,
        code: grid.allClientViewCode,
        selected: grid.allClientViewId,
      });
      await this.$store.dispatch('setTab', 'universe');
      await this.$store.dispatch('setSearchTerm', '');
      await this.$store.dispatch('closeNotification');
    },
    async updateClient(client, clientName, clientCode, hasReplacements) {
      await this.$store.dispatch('setClient', {
        listVisible: false,
        name: clientName,
        code: clientCode,
        selected: client,
        hasReplacements,
      });
      await this.$store.dispatch('setProduct', {
        code: grid.defaultProductCode,
        name: grid.defaultProductName,
        selected: grid.defaultSelectedProductId,
      });
      await this.$store.dispatch('setTab', 'universe');
      EventBus.$emit('search-clear');
    },
  },
  async created() {
    // if the fiduciary client id is valid it will hit the route
    if (this.isValidClientId) {
      await this.updateClient(
        this.clientData.id,
        this.clientData.client.clientName,
        this.clientData.client.clientCode,
        this.clientData.hasReplacements,
      );
    }
  },
  async beforeRouteLeave(to, from, next) {
    if (to.name !== 'createCustomFunds') {
      // Maintain client selection while navigating to custom fund page.
      await this.eraseGridData();
    }
    next();
  },
};
</script>

<style lang="scss">
.grid-container {
  display: flex;
  flex-direction: column;
  height: calc(100vh - #{$fc-main-header-heigh} - #{$fc-main-footer-heigh});
}
.grid-wrapper {
  width: calc(100% - 85px);
}
</style>
